import React, { useState } from 'react';
import styled from 'styled-components';
import {
  CancelOutlined,
  Start,
  Visibility,
  FileDownload,
  OfflinePin,
  ArrowCircleDown,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { publicHeadCells } from './headCells';
import { OFFER_STATUS } from './constants';
import { offerStatusFilter } from '../filters';
import { GS_FILTER_API_TYPE, GSTableFilter } from '../GSTableFilter';
import { MONITOR_LIST_TEXT } from '../../../lang';
import {
  useGetEmailList,
  type GSEmailData,
  useRevokeOfferResult,
  useReleaseOfferResult,
  useConfirmRSFSubmission,
} from '../../../graphql';
import {
  Alert,
  Loading,
  Button,
  IconButton,
  pageRowsSelectOptions,
  AdvancedTable,
  type AlertDetails,
  type HeadCell,
} from '../../../../../components';
import { postRequest, type PostRequestData } from '../../../../../utils';
import { OneOffDialog, type OneOffDialogConfig } from '../../../components';

export const GSEmailList: React.FunctionComponent = () => {
  const filterButtons = [
    {
      name: 'All',
      filter: offerStatusFilter(Object.keys(OFFER_STATUS)),
    },
    {
      name: 'Pending RSF',
      filter: offerStatusFilter([OFFER_STATUS.PROVISIONING_PENDING_RSF]),
    },
    {
      name: 'Ready to Release',
      filter: offerStatusFilter([OFFER_STATUS.READY_TO_RELEASE]),
    },
    {
      name: 'Releasing',
      filter: offerStatusFilter([OFFER_STATUS.RELEASING]),
    },
    {
      name: 'Released',
      filter: offerStatusFilter([OFFER_STATUS.RELEASED]),
    },
    {
      name: 'Revoked',
      filter: offerStatusFilter([OFFER_STATUS.REVOKED]),
    },
  ];

  const [activateButton, setActivateButton] = useState<number>(0);

  const handleChangeFilterButton = (button: number) => () => {
    setActivateButton(button);
  };

  const buttonArea: React.ReactNode = (
    <div className="flex flex-row">
      {filterButtons.map((item, index) => {
        return (
          <div key={index} className="mr-4">
            <Button
              sx={{ textTransform: 'none' }}
              onClick={handleChangeFilterButton(index)}
              variant={activateButton === index ? 'contained' : 'outlined'}
            >
              {item.name}
            </Button>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <GSEmailListPage
        key={activateButton}
        activateButton={activateButton}
        filter={filterButtons[activateButton]?.filter}
      >
        {buttonArea}
      </GSEmailListPage>
    </>
  );
};

const GSEmailListPage: React.FC<{
  activateButton: number;
  filter?: any;
  children?: React.ReactNode;
}> = props => {
  const { activateButton, filter: initialFilter = {}, children } = props;

  const [page, setPage] = useState(1); // default current page is 1

  const [selectRowsLimit, setSelectRowsLimit] = useState(
    pageRowsSelectOptions[0].value as number, // default rows per page is the first option
  );
  const [checkedRows, setCheckedRows] = useState<number[]>([]); // to store all index of checked row(s)
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [oneOffDialogConfig, setOpenOneOffDialog] =
    useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [filter, setFilter] = useState<any>(initialFilter);
  const [appliedFilter, setAppliedFilter] = useState<any>(null);

  const {
    emailListData,
    loading,
    error: listError,
    refetch: refetchEmailList,
  } = useGetEmailList(filter, {}, { page, limit: selectRowsLimit });
  const { emailList, totalPage, count } = emailListData;

  const visibleData: GSEmailData[] = emailList ?? [];
  const pageCount: number = totalPage ?? 1;
  const recordsCount: number = count ?? 0;

  const headCells: Array<HeadCell<GSEmailData>> = [
    ...publicHeadCells,
    {
      id: 'isFirmOffer',
      label: MONITOR_LIST_TEXT.OFFER,
      isSortable: false,
      render: (row: GSEmailData) => (row.isFirmOffer ? 'Firm' : 'Conditional'),
    },
    {
      id: 'releaseDate',
      label: MONITOR_LIST_TEXT.RELEASE_DATE,
      isSortable: true,
      render: (row: GSEmailData) =>
        row.releaseDate
          ? dayjs(row.releaseDate).format('DD/MM/YYYY,HH:mm')
          : MONITOR_LIST_TEXT.NOT_YET_RELEASED,
    },
    {
      id: 'action',
      label: MONITOR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: GSEmailData) => action(row),
    },
  ];

  const confirmRSFSubmission = useConfirmRSFSubmission();
  const releaseResult = useReleaseOfferResult();
  const revokeResult = useRevokeOfferResult();

  const handleRSFConfirmation = async (
    applicationIds: string[],
  ): Promise<void> => {
    try {
      const res = await confirmRSFSubmission({
        variables: {
          input: {
            applicationIds,
          },
        },
      });

      if (res.data?.confirmRSFSubmission?.needAlert === false) {
        setAlertDetails({
          alertContent:
            'Confirmed Successfully. You can find the offer(s) in Ready to Release list.',
          alertType: 'success',
        });
      } else {
        setAlertDetails({
          alertContent: res.data?.confirmRSFSubmission?.failedList,
          alertType: 'warning',
        });
      }
    } catch (error) {
      setAlertDetails({
        alertContent:
          `${
            applicationIds?.length === 1 ? 'Confirmation' : 'Batch confirmation'
          } failed.` + (error as Error).message,
        alertType: 'error',
      });
    } finally {
      setOpenAlert(true);

      refetchEmailList();
    }
  };

  const handleAnnounceResult = async (applicationId: string): Promise<void> => {
    try {
      await releaseResult({
        variables: {
          input: {
            applicationIds: [applicationId],
          },
        },
      });
      setAlertDetails({
        alertContent: 'Announced the Result Successfully.',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'warning',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const handleRevokeResult = async (applicationId: string): Promise<void> => {
    try {
      await revokeResult({
        variables: {
          input: {
            applicationIds: [applicationId],
          },
        },
      });
      setAlertDetails({
        alertContent: 'Revoked the Result Successfully.',
        alertType: 'success',
      });
      refetchEmailList();
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'warning',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const action = (row: GSEmailData): React.ReactNode => {
    const { applicationNo, applicationId } = row;
    const ViewMonitorButton: React.ReactNode = (
      <IconButton
        onClick={handleViewApplication(applicationId)}
        color="primary"
        icon={<Visibility />}
      />
    );

    const CheckRSFButton: React.ReactNode = (
      <IconButton
        title="Confirm RSF Uploaded"
        onClick={async () => {
          await handleRSFConfirmation([applicationId]);
        }}
        color={
          row.status === OFFER_STATUS.PROVISIONING_PENDING_RSF
            ? 'primary'
            : 'inherit'
        }
        icon={<OfflinePin />}
      />
    );

    const AnnounceResultButton: React.ReactNode = (
      <IconButton
        title="Announce the result"
        onClick={async () => {
          await handleAnnounceResult(applicationId);
        }}
        color="primary"
        icon={<Start />}
      />
    );

    const RevokeResultButton: React.ReactNode = (
      <IconButton
        title="Revoke the result"
        color="primary"
        onClick={async () => {
          await handleRevokeResult(applicationId);
        }}
        icon={<CancelOutlined />}
      />
    );
    const DownLoadOneoff: React.ReactNode = (
      <IconButton
        color="primary"
        onClick={() => {
          setOpenOneOffDialog({
            open: true,
            applicationId,
            applicationNo,
          });
        }}
        title="Download Profile"
        icon={<ArrowCircleDown />}
      />
    );
    const activateButtonActionMap: Record<number, React.ReactNode[]> = {
      0: [],
      1: [CheckRSFButton], // Pending RSF
      2: [AnnounceResultButton],
      3: [RevokeResultButton],
      4: [],
      5: [AnnounceResultButton],
    };
    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          {[
            ...activateButtonActionMap[activateButton],
            ViewMonitorButton,
            DownLoadOneoff,
          ]?.map((button, index) => {
            return <span key={index}>{button}</span>;
          })}
        </div>
      </div>
    );
  };

  const handleViewApplication =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const handleRequestSort = (property: keyof GSEmailData): void => {
    // TODO: Get the sorted data from the server
  };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectRowsLimit(parseInt(event.target.value));
    setPage(1);
  };

  const handleChangeCheckedIndex = (checkedIndex: number[]): void => {
    setCheckedRows(checkedIndex);
  };

  const handleChangeCheckAll = (checkAll: boolean): void => {
    setCheckedAll(checkAll);
  };

  if (listError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (listError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const disableDownload = !checkedRows.length || !visibleData.length; // no application has been selected

  const handleExportCSV = async (): Promise<void> => {
    if (disableDownload) return;
    const exportApplicationIds: string[] = checkedRows.map(rowIndex => {
      const { applicationId } = visibleData[rowIndex];
      return applicationId;
    });

    try {
      const postData: PostRequestData = {
        url: `/eadmin-operation/offer/export/rsf`,
        data: JSON.stringify({ applicationIds: exportApplicationIds }),
        responseType: 'blob',
      };

      const response = await postRequest(postData);

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const formattedDateTime = dayjs().format('DD/MM/YYYY_HH-mm-ss');
      link.setAttribute('download', `export-${formattedDateTime}.csv`);

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url);
    } catch (error) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: 'Error exporting file: ' + (error as Error).message,
        alertType: 'error',
      });
    }
  };

  const handleBatchConfirmRSFUploaded = async (): Promise<void> => {
    if (disableDownload) return;
    const confirmApplicationIds: string[] = checkedRows.map(rowIndex => {
      const { applicationId } = visibleData[rowIndex];
      return applicationId;
    });
    await handleRSFConfirmation(confirmApplicationIds);
  };

  const { open: openDialog } = oneOffDialogConfig;

  return (
    <>
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <GSTableFilter
        apiType={GS_FILTER_API_TYPE.offerList}
        initialAppliedFilter={appliedFilter}
        setFilter={(filter: any, appliedFilter: any) => {
          setPage(1);
          setFilter({
            ...filter,
            ...initialFilter,
          });
          setAppliedFilter(appliedFilter);
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <Container className="mt-6">
          <AdvancedTable<GSEmailData>
            showCheckBox={activateButton === 1}
            checkBoxProps={{
              checkedIndex: checkedRows,
              onChangeCheckedIndex: handleChangeCheckedIndex,
              checkedAll,
              onCheckAll: handleChangeCheckAll,
            }}
            extendFunctionAreas={
              <div>
                {children}

                {activateButton === 1 && (
                  <div
                    className="flex flex-row items-center cursor-pointer mt-2"
                    aria-disabled={disableDownload}
                  >
                    <div
                      onClick={handleExportCSV}
                      className="flex flex-row items-center cursor-pointer"
                    >
                      <IconButton
                        title={MONITOR_LIST_TEXT.EXPORT_CSV}
                        disabled={disableDownload}
                        icon={
                          <FileDownload
                            color={disableDownload ? 'inherit' : 'primary'}
                          />
                        }
                      />
                      <h2
                        className={`ml-1 ${
                          disableDownload ? 'text-[#C5C5C5]' : 'text-[#951D1D]'
                        }`}
                      >
                        {MONITOR_LIST_TEXT.EXPORT_CSV}
                      </h2>
                    </div>
                    <div
                      onClick={handleBatchConfirmRSFUploaded}
                      className="flex flex-row items-center cursor-pointer ml-4"
                    >
                      <IconButton
                        title={MONITOR_LIST_TEXT.CHECK_STATUS}
                        disabled={disableDownload}
                        icon={
                          <OfflinePin
                            color={disableDownload ? 'inherit' : 'primary'}
                          />
                        }
                      />
                      <h2
                        className={`ml-1 ${
                          disableDownload ? 'text-[#C5C5C5]' : 'text-[#951D1D]'
                        }`}
                      >
                        {MONITOR_LIST_TEXT.CONFIRM_RSF}
                      </h2>
                    </div>
                  </div>
                )}
              </div>
            }
            tableProps={{
              headCells,
              data: visibleData,
              handleRequestSort,
            }}
            advancedPaginationProps={{
              page,
              count: pageCount,
              recordText: `${recordsCount} records`,
              rowsPerPageOptions: pageRowsSelectOptions,
              rowsPerPage: selectRowsLimit,
              onChangeRowsPerPage: handleChangeRowsPerPage,
            }}
            handleChangePage={handleChangePage}
          />
        </Container>
      )}
    </>
  );
};

const Container = styled.div`
  h1 {
    text-align: center;
    font-size: 2rem;
    margin: 2rem 0;
  }
`;
